/**
 * RouteError — React Router errorElement fallback.
 *
 * Catches errors thrown while matching/rendering a route, including lazy-chunk
 * load failures (common after a redeploy invalidates old hashed chunks) and
 * loader/render exceptions. Without this, RR6 shows its bare default error page.
 */

import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';

export default function RouteError() {
  const error = useRouteError();

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : 'An unexpected error occurred while loading this page.';

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center p-8 text-center">
      <AlertTriangle className="size-10 text-destructive" aria-hidden="true" />
      <h1 className="mt-4 text-2xl font-semibold tracking-tight text-foreground">
        Something went wrong
      </h1>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        This page failed to load. A reload usually fixes a stale or interrupted load (for example
        after the app was updated).
      </p>
      <pre className="mt-3 max-w-lg overflow-auto rounded-md bg-muted px-3 py-2 text-left text-xs text-muted-foreground">
        {message}
      </pre>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Button onClick={() => window.location.reload()}>Reload page</Button>
        <Button variant="outline" asChild>
          <Link to="/dashboard">Back to dashboard</Link>
        </Button>
      </div>
    </div>
  );
}
